import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  getProfile,
  getSkills,
  getEducation,
  getExperience,
} from "../api/profileAPI";
import { getPublishedPosts } from "../api/blogAPI";
import { fetchCertificates } from "../api/certificateAPI";
import { fetchPortofolios } from "../api/portofolioAPI";
import { trackVisit } from "../api/analyticsAPI";
import ExperienceMediaCards from "../components/ExperienceMediaCards";
import { sortExperienceGrouped, calcDuration } from "../utils/sortExperience";

// ─── Google Fonts injection ───────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById("aurora-fonts")) return;
  const link = document.createElement("link");
  link.id = "aurora-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;1,400&display=swap";
  document.head.appendChild(link);
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:       "#020617",
  surface:  "#0F172A",
  elevated: "#1E293B",
  border:   "rgba(148,163,184,0.12)",
  accent:   "#22C55E",
  accentDim:"rgba(34,197,94,0.15)",
  blue:     "#3B82F6",
  blueDim:  "rgba(59,130,246,0.12)",
  text:     "#F8FAFC",
  muted:    "#94A3B8",
  dim:      "#475569",
};

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";

// ─── Inject keyframes ─────────────────────────────────────────────────────────
const STYLES = `
  @keyframes aurora-blob {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(40px,-30px) scale(1.1); }
    66%      { transform: translate(-20px,20px) scale(0.95); }
  }
  @keyframes aurora-in {
    from { opacity:0; transform: translateY(28px); }
    to   { opacity:1; transform: translateY(0); }
  }
  @keyframes aurora-slide-left {
    from { opacity:0; transform: translateX(-32px); }
    to   { opacity:1; transform: translateX(0); }
  }
  @keyframes aurora-slide-right {
    from { opacity:0; transform: translateX(32px); }
    to   { opacity:1; transform: translateX(0); }
  }
  @keyframes aurora-fade {
    from { opacity:0; }
    to   { opacity:1; }
  }
  @keyframes aurora-float {
    0%,100% { transform: translateY(0); }
    50%     { transform: translateY(-8px); }
  }
  @keyframes aurora-spin-slow {
    to { transform: rotate(360deg); }
  }
  @keyframes aurora-pulse-ring {
    0%   { transform: scale(1);   opacity:.6; }
    100% { transform: scale(1.8); opacity:0; }
  }
  @keyframes aurora-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes aurora-type {
    from { width: 0; }
    to   { width: 100%; }
  }
  @keyframes aurora-cursor {
    0%,100% { opacity:1; }
    50%     { opacity:0; }
  }
  .aurora-in         { animation: aurora-in 0.65s cubic-bezier(0.16,1,0.3,1) both; }
  .aurora-slide-l    { animation: aurora-slide-left  0.65s cubic-bezier(0.16,1,0.3,1) both; }
  .aurora-slide-r    { animation: aurora-slide-right 0.65s cubic-bezier(0.16,1,0.3,1) both; }
  .aurora-fade       { animation: aurora-fade 0.5s ease both; }
  .aurora-float      { animation: aurora-float 4s ease-in-out infinite; }
  .aurora-shimmer    {
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
    background-size: 200% 100%;
    animation: aurora-shimmer 2.5s linear infinite;
  }
  .delay-100  { animation-delay: .10s; }
  .delay-200  { animation-delay: .20s; }
  .delay-300  { animation-delay: .30s; }
  .delay-400  { animation-delay: .40s; }
  .delay-500  { animation-delay: .50s; }
  .delay-600  { animation-delay: .60s; }
  .delay-700  { animation-delay: .70s; }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

const injectStyles = () => {
  if (document.getElementById("aurora-styles")) return;
  const s = document.createElement("style");
  s.id = "aurora-styles";
  s.textContent = STYLES;
  document.head.appendChild(s);
};

// ─── Scroll-reveal hook ───────────────────────────────────────────────────────
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

// ─── Ambient background blobs ─────────────────────────────────────────────────
const AmbientBlobs = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
    <div style={{
      position:"absolute", top:"-15%", left:"-10%",
      width:600, height:600, borderRadius:"50%",
      background:"radial-gradient(circle, rgba(34,197,94,0.07) 0%, transparent 70%)",
      animation:"aurora-blob 18s ease-in-out infinite",
    }} />
    <div style={{
      position:"absolute", top:"40%", right:"-15%",
      width:500, height:500, borderRadius:"50%",
      background:"radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 70%)",
      animation:"aurora-blob 22s ease-in-out infinite reverse",
      animationDelay:"-8s",
    }} />
    <div style={{
      position:"absolute", bottom:"-10%", left:"30%",
      width:400, height:400, borderRadius:"50%",
      background:"radial-gradient(circle, rgba(168,85,247,0.05) 0%, transparent 70%)",
      animation:"aurora-blob 26s ease-in-out infinite",
      animationDelay:"-14s",
    }} />
    {/* Grid overlay */}
    <div style={{
      position:"absolute", inset:0,
      backgroundImage:`linear-gradient(rgba(148,163,184,0.03) 1px, transparent 1px),
                       linear-gradient(90deg, rgba(148,163,184,0.03) 1px, transparent 1px)`,
      backgroundSize:"64px 64px",
    }} />
  </div>
);

// ─── Navbar ───────────────────────────────────────────────────────────────────
const NAV_ITEMS = ["Home","About","Skills","Experience","Education","Portfolio","Certificates","Blog","Contact"];

const Navbar = ({ activeSection }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id.toLowerCase());
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header style={{
      position:"fixed", top:0, left:0, right:0, zIndex:50,
      transition:"all 0.4s cubic-bezier(0.16,1,0.3,1)",
      background: scrolled ? "rgba(2,6,23,0.85)" : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
    }}>
      <div style={{ maxWidth:1200, margin:"0 auto", padding:"0 24px", height:64,
        display:"flex", alignItems:"center", justifyContent:"space-between" }}>

        {/* Logo */}
        <button onClick={() => scrollTo("home")} style={{
          fontFamily:"'Space Grotesk', sans-serif",
          fontWeight:700, fontSize:20, color:T.text,
          background:"none", border:"none", cursor:"pointer", padding:0,
        }}>
          AH<span style={{ color:T.accent }}>.</span>
        </button>

        {/* Desktop nav */}
        <nav style={{ display:"flex", alignItems:"center", gap:4 }} className="aurora-desktop-nav">
          {NAV_ITEMS.map((item) => {
            const active = activeSection === item.toLowerCase();
            return (
              <button key={item} onClick={() => scrollTo(item)}
                aria-current={active ? "true" : undefined}
                style={{
                  fontFamily:"'DM Sans', sans-serif",
                  fontWeight: active ? 600 : 400,
                  fontSize:14, padding:"8px 16px",
                  borderRadius:8, border:"none", cursor:"pointer",
                  background: active ? T.accentDim : "transparent",
                  color: active ? T.accent : T.muted,
                  transition:"all 0.2s ease",
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.color = T.muted; }}
              >
                {item}
              </button>
            );
          })}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="aurora-mobile-btn"
          onClick={() => setMenuOpen(v => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="aurora-mobile-nav"
          style={{
            display:"none", background:"none", border:"none",
            cursor:"pointer", padding:8, color:T.muted,
          }}
        >
          <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div id="aurora-mobile-nav" style={{
          background:"rgba(2,6,23,0.97)", backdropFilter:"blur(20px)",
          borderTop:`1px solid ${T.border}`, padding:"12px 24px 20px",
        }}>
          {NAV_ITEMS.map((item, i) => (
            <button key={item} onClick={() => scrollTo(item)}
              aria-current={activeSection === item.toLowerCase() ? "true" : undefined}
              className={`aurora-in delay-${Math.min(i * 100, 700)}`}
              style={{
                display:"block", width:"100%", textAlign:"left",
                fontFamily:"'DM Sans', sans-serif", fontWeight:500, fontSize:15,
                padding:"12px 16px", borderRadius:8, border:"none", cursor:"pointer",
                background: activeSection === item.toLowerCase() ? T.accentDim : "transparent",
                color: activeSection === item.toLowerCase() ? T.accent : T.muted,
                marginBottom:4,
              }}
            >
              {item}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .aurora-desktop-nav { display: none !important; }
          .aurora-mobile-btn  { display: block !important; }
        }
      `}</style>
    </header>
  );
};

// ─── Section wrapper with reveal ──────────────────────────────────────────────
const Section = ({ id, children, style = {} }) => {
  const [ref, visible] = useReveal(0.1);
  return (
    <section id={id} ref={ref} style={{
      minHeight:"100vh", padding:"120px 24px 80px",
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(32px)",
      transition:"opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)",
      ...style,
    }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>
        {children}
      </div>
    </section>
  );
};

// ─── Section heading ──────────────────────────────────────────────────────────
const SectionHeading = ({ label, title, sub }) => (
  <div style={{ marginBottom:56 }}>
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
      <div style={{ height:1, width:32, background:T.accent }} />
      <span style={{
        fontFamily:"'DM Sans', sans-serif", fontSize:11,
        fontWeight:700, letterSpacing:"0.3em", textTransform:"uppercase",
        color:T.accent,
      }}>
        {label}
      </span>
    </div>
    <h2 style={{
      fontFamily:"'Space Grotesk', sans-serif",
      fontWeight:700, fontSize:"clamp(28px, 5vw, 48px)",
      color:T.text, lineHeight:1.15, marginBottom:sub ? 12 : 0,
    }}>
      {title}
    </h2>
    {sub && <p style={{ fontFamily:"'DM Sans', sans-serif", color:T.muted, fontSize:16, maxWidth:560 }}>{sub}</p>}
  </div>
);

// ─── Glass card ───────────────────────────────────────────────────────────────
const GlassCard = ({ children, style = {}, hover = true, className = "" }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: hovered ? "rgba(30,41,59,0.9)" : "rgba(15,23,42,0.7)",
        border: `1px solid ${hovered ? "rgba(148,163,184,0.25)" : T.border}`,
        borderRadius:16,
        backdropFilter:"blur(12px)",
        transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
        transform: hovered ? "translateY(-3px)" : "none",
        boxShadow: hovered ? "0 20px 60px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.2)",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// ─── Typing animation component ───────────────────────────────────────────────
const TypewriterText = ({ texts, interval = 3000 }) => {
  const [idx, setIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[idx];
    let timeout;
    if (!deleting && displayed.length < current.length) {
      timeout = setTimeout(() => setDisplayed(current.slice(0, displayed.length + 1)), 60);
    } else if (!deleting && displayed.length === current.length) {
      timeout = setTimeout(() => setDeleting(true), interval);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setIdx((i) => (i + 1) % texts.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, deleting, idx, texts, interval]);

  return (
    <span>
      <span style={{ color:T.accent }}>{displayed}</span>
      <span style={{ animation:"aurora-cursor 1s step-end infinite", color:T.accent }}>|</span>
    </span>
  );
};

// ─── Stat counter ─────────────────────────────────────────────────────────────
const AnimatedCounter = ({ target, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, visible] = useReveal(0.3);

  useEffect(() => {
    if (!visible) return;
    const duration = 1200;
    const step = target / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setCount(Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [visible, target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// ─── Skill bar ────────────────────────────────────────────────────────────────
const SkillBar = ({ name, level, delay = 0 }) => {
  const [ref, visible] = useReveal(0.2);
  const pct = Math.min(Math.max(level || 70, 0), 100);
  return (
    <div ref={ref} style={{ marginBottom:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
        <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:13, fontWeight:500, color:T.text }}>
          {name}
        </span>
        <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:T.accent }}>{pct}%</span>
      </div>
      <div style={{ height:4, background:"rgba(148,163,184,0.1)", borderRadius:99 }}>
        <div style={{
          height:"100%", borderRadius:99,
          background:`linear-gradient(90deg, ${T.accent}, ${T.blue})`,
          width: visible ? `${pct}%` : "0%",
          transition: `width 1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        }} />
      </div>
    </div>
  );
};

// ─── HERO SECTION ─────────────────────────────────────────────────────────────
const HeroSection = ({ profile }) => {
  const name = profile?.full_name || "Affan Haidar";
  const photo = resolveImg(profile?.photo_url);
  const headline = profile?.headline || "";
  const roles = headline
    ? headline.split("·").map(s => s.trim()).filter(Boolean)
    : ["NOC Engineer", "Network Analyst", "Tech Enthusiast"];
  const bio = profile?.description || "Building robust network infrastructures and crafting digital experiences.";
  const social = [
    profile?.show_github    && profile?.social_github    ? { label:"GitHub",    href: profile.social_github,    icon:"M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.605-3.369-1.34-3.369-1.34-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836a9.59 9.59 0 012.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.933.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.741 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" } : null,
    profile?.show_linkedin  && profile?.social_linkedin  ? { label:"LinkedIn",  href: profile.social_linkedin,  icon:"M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" } : null,
    profile?.show_instagram && profile?.social_instagram ? { label:"Instagram", href: profile.social_instagram, icon:"M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" } : null,
  ].filter(Boolean);

  return (
    <section id="home" style={{
      minHeight:"100vh", padding:"0 24px",
      display:"flex", alignItems:"center",
      position:"relative",
    }}>
      <div style={{ maxWidth:1200, margin:"0 auto", width:"100%", paddingTop:80 }}>
        <div style={{
          display:"grid",
          gridTemplateColumns:"1fr auto",
          gap:64,
          alignItems:"center",
        }} className="aurora-hero-grid">

          {/* Left — text */}
          <div>
            {/* Badge */}
            <div className="aurora-in" style={{
              display:"inline-flex", alignItems:"center", gap:8,
              padding:"6px 16px", borderRadius:999,
              background:T.accentDim, border:`1px solid rgba(34,197,94,0.25)`,
              marginBottom:28,
            }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:T.accent,
                animation:"aurora-pulse-ring 1.5s ease-out infinite" }} />
              <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12,
                fontWeight:600, color:T.accent, letterSpacing:"0.05em" }}>
                Available for work
              </span>
            </div>

            {/* Name */}
            <h1 className="aurora-in delay-100" style={{
              fontFamily:"'Space Grotesk', sans-serif",
              fontWeight:700,
              fontSize:"clamp(40px, 7vw, 80px)",
              lineHeight:1.05,
              color:T.text,
              marginBottom:16,
            }}>
              {name.split(" ").map((word, i) => (
                <span key={i} style={{
                  display:"block",
                  color: i === (name.split(" ").length - 1) ? "transparent" : T.text,
                  WebkitTextStroke: i === (name.split(" ").length - 1) ? `1px ${T.text}` : "none",
                }}>
                  {word}
                </span>
              ))}
            </h1>

            {/* Typewriter */}
            <div className="aurora-in delay-200" style={{
              fontFamily:"'Space Grotesk', sans-serif",
              fontSize:"clamp(18px, 3vw, 26px)",
              fontWeight:500,
              color:T.muted,
              marginBottom:24,
              minHeight:40,
            }}>
              <TypewriterText texts={roles} />
            </div>

            {/* Bio */}
            <p className="aurora-in delay-300" style={{
              fontFamily:"'DM Sans', sans-serif",
              fontSize:16, lineHeight:1.75,
              color:T.muted, maxWidth:520, marginBottom:40,
            }}>
              {bio}
            </p>

            {/* CTAs */}
            <div className="aurora-in delay-400" style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:48 }}>
              <button
                onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior:"smooth" }); }}
                style={{
                  fontFamily:"'DM Sans', sans-serif", fontWeight:600, fontSize:14,
                  padding:"14px 28px", borderRadius:12,
                  background:`linear-gradient(135deg, ${T.accent}, #16a34a)`,
                  color:"#020617", border:"none", cursor:"pointer",
                  boxShadow:`0 0 24px ${T.accentDim}`,
                  transition:"all 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 30px rgba(34,197,94,0.4)`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 0 24px ${T.accentDim}`; }}
              >
                Get in touch
              </button>
              <button
                onClick={() => { const el = document.getElementById("about"); if (el) el.scrollIntoView({ behavior:"smooth" }); }}
                style={{
                  fontFamily:"'DM Sans', sans-serif", fontWeight:600, fontSize:14,
                  padding:"14px 28px", borderRadius:12,
                  background:"transparent", border:`1px solid ${T.border}`,
                  color:T.text, cursor:"pointer",
                  transition:"all 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.4)"; e.currentTarget.style.background = "rgba(15,23,42,0.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "transparent"; }}
              >
                View work ↓
              </button>
              {profile?.cv_pdf_url && (
                <a
                  href={resolveImg(profile.cv_pdf_url)}
                  download
                  style={{
                    fontFamily:"'DM Sans', sans-serif", fontWeight:600, fontSize:14,
                    padding:"14px 28px", borderRadius:12,
                    background:"transparent", border:`1px solid ${T.border}`,
                    color:T.muted, cursor:"pointer", textDecoration:"none",
                    transition:"all 0.2s ease", display:"inline-block",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.4)"; e.currentTarget.style.color = T.text; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
                >
                  Download CV (PDF)
                </a>
              )}
              {profile?.cv_docx_url && (
                <a
                  href={resolveImg(profile.cv_docx_url)}
                  download
                  style={{
                    fontFamily:"'DM Sans', sans-serif", fontWeight:600, fontSize:14,
                    padding:"14px 28px", borderRadius:12,
                    background:"transparent", border:`1px solid ${T.border}`,
                    color:T.muted, cursor:"pointer", textDecoration:"none",
                    transition:"all 0.2s ease", display:"inline-block",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.4)"; e.currentTarget.style.color = T.text; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
                >
                  Download CV (DOCX)
                </a>
              )}
            </div>

            {/* Social */}
            <div className="aurora-in delay-500" style={{ display:"flex", gap:12 }}>
              {social.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  aria-label={s.label}
                  style={{
                    width:40, height:40, borderRadius:10,
                    background:"rgba(15,23,42,0.7)",
                    border:`1px solid ${T.border}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:T.dim, transition:"all 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.accent; e.currentTarget.style.borderColor = "rgba(34,197,94,0.3)"; e.currentTarget.style.background = T.accentDim; }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.dim; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "rgba(15,23,42,0.7)"; }}
                >
                  <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d={s.icon} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Right — photo */}
          <div className="aurora-in delay-300 aurora-hero-photo" style={{ position:"relative" }}>
            {/* Spinning ring */}
            <div style={{
              position:"absolute", inset:-16,
              borderRadius:"50%",
              border:"1px dashed rgba(34,197,94,0.2)",
              animation:"aurora-spin-slow 20s linear infinite",
            }} />
            <div style={{
              position:"absolute", inset:-32,
              borderRadius:"50%",
              border:"1px dashed rgba(59,130,246,0.15)",
              animation:"aurora-spin-slow 30s linear infinite reverse",
            }} />

            {/* Glow */}
            <div style={{
              position:"absolute", inset:0,
              borderRadius:"50%",
              background:`radial-gradient(circle, ${T.accentDim} 0%, transparent 70%)`,
              filter:"blur(20px)",
            }} />

            {/* Photo */}
            <div style={{
              width:280, height:280, borderRadius:"50%",
              overflow:"hidden",
              border:`2px solid rgba(34,197,94,0.3)`,
              position:"relative", zIndex:1,
              background:T.surface,
              animation:"aurora-float 5s ease-in-out infinite",
            }}>
              {photo
                ? <img src={photo} alt={name} style={{ width:"100%", height:"100%", objectFit:"cover" }} fetchpriority="high" />
                : <div style={{
                    width:"100%", height:"100%",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontFamily:"'Space Grotesk', sans-serif", fontWeight:700,
                    fontSize:64, color:T.accent,
                  }}>{name[0]}</div>
              }
            </div>

            {/* Floating badge */}
            <div className="aurora-float" style={{
              position:"absolute", bottom:-16, left:-40,
              padding:"10px 16px", borderRadius:12,
              background:"rgba(15,23,42,0.95)",
              border:`1px solid ${T.border}`,
              backdropFilter:"blur(12px)",
              zIndex:2, animationDelay:"-2s",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{
                  width:32, height:32, borderRadius:8,
                  background:T.accentDim,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width={16} height={16} fill="none" stroke={T.accent} viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:11, fontWeight:700, color:T.text }}>
                    {headline?.split("·")[0]?.trim() || roles[0]}
                  </div>
                  <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:10, color:T.muted }}>Active</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div aria-hidden="true" style={{
          position:"absolute", bottom:40, left:"50%", transform:"translateX(-50%)",
          display:"flex", flexDirection:"column", alignItems:"center", gap:8,
        }}>
          <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:11, color:T.dim, letterSpacing:"0.15em", textTransform:"uppercase" }}>
            Scroll
          </span>
          <div style={{
            width:1, height:40,
            background:`linear-gradient(to bottom, ${T.dim}, transparent)`,
            animation:"aurora-float 2s ease-in-out infinite",
          }} />
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .aurora-hero-grid { grid-template-columns: 1fr !important; }
          .aurora-hero-photo { display: none !important; }
        }
      `}</style>
    </section>
  );
};

// ─── ABOUT SECTION ────────────────────────────────────────────────────────────
const AboutSection = ({ profile }) => {
  const [ref, visible] = useReveal(0.1);
  const headline = profile?.headline || "";
  const stats = [
    { label:"Years Experience", value:3,  suffix:"+" },
    { label:"Projects Done",    value:20, suffix:"+" },
    { label:"Certifications",   value:5,  suffix:"" },
  ];

  return (
    <Section id="about">
      <div ref={ref} style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:64, alignItems:"center" }}
        className="aurora-about-grid">
        {/* Left */}
        <div>
          <SectionHeading label="About me" title="Passionate about networks & code." />
          <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:16, lineHeight:1.8, color:T.muted, marginBottom:24 }}>
            {profile?.description || "I'm a network operations professional with a passion for building resilient systems and elegant interfaces. I bridge the gap between infrastructure reliability and modern software development."}
          </p>
          {profile?.doing_now && (
            <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:16, lineHeight:1.8, color:T.muted, marginBottom:40 }}>
              Currently: {profile.doing_now}
            </p>
          )}

          {/* Stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:20 }}>
            {stats.map((s, i) => (
              <GlassCard key={i} style={{ padding:"20px 16px", textAlign:"center" }}>
                <div style={{
                  fontFamily:"'Space Grotesk', sans-serif",
                  fontSize:32, fontWeight:700, color:T.accent, lineHeight:1,
                }}>
                  {visible ? <AnimatedCounter target={s.value} suffix={s.suffix} /> : `0${s.suffix}`}
                </div>
                <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:T.muted, marginTop:8 }}>
                  {s.label}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>

        {/* Right — info cards */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {[
            { icon:"🌐", label:"Location",  value: "Indonesia" },
            { icon:"📧", label:"Email",     value: profile?.social_email || "affan@example.com" },
            { icon:"💼", label:"Role",      value: headline?.split("·")[0]?.trim() || "NOC Engineer" },
            { icon:"📋", label:"Now",       value: profile?.doing_now || "Network & Security" },
          ].map((item, i) => (
            <GlassCard key={i} style={{ padding:"16px 20px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <div style={{
                  width:40, height:40, borderRadius:10, fontSize:18,
                  background:`linear-gradient(135deg, ${T.accentDim}, ${T.blueDim})`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  flexShrink:0,
                }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:11, color:T.dim, fontWeight:500, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                    {item.label}
                  </div>
                  <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:T.text, fontWeight:500, marginTop:2 }}>
                    {item.value}
                  </div>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .aurora-about-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
};

// ─── WHAT I DO SECTION ────────────────────────────────────────────────────────
const WHAT_I_DO = [
  {
    title: "Cybersecurity",
    color: T.accent,
    points: ["Vulnerability assessment (Nmap, Burp Suite, DVWA)", "Blue Team: Kibana ELK, Wazuh SIEM, Wireshark, Nessus", "CTF labs: recon, enumeration, basic exploitation", "Risk-based prioritization & reproducible reporting"],
  },
  {
    title: "Network (NOC L1)",
    color: T.blue,
    points: ["Real-time monitoring: Grafana, OpManager, Dynatrace, Elastic", "Incident triage & escalation to L2/L3 — 24×7 rotational shift", "UiPath automation for NOC report generation", "Excel trend mapping & Jira ticketing"],
  },
  {
    title: "Full-Stack Development",
    color: "#22d3ee",
    points: ["React.js front-end with responsive, accessible UIs", "Express + MySQL backend with REST API", "Functional, regression & performance QA", "Multer uploads, JWT auth, analytics tracking"],
  },
];

const WhatIDoSection = ({ profile }) => {
  if (profile?.show_what_i_do === 0) return null;
  return (
    <Section id="whatido">
      <SectionHeading label="What I Do" title="Areas of expertise." sub="Where I focus my energy." />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:20 }}>
        {WHAT_I_DO.map((item) => (
          <GlassCard key={item.title} style={{ padding:28 }}>
            <div style={{
              fontFamily:"'Space Grotesk', sans-serif", fontSize:13, fontWeight:700,
              color: item.color, textTransform:"uppercase", letterSpacing:"0.15em", marginBottom:20,
            }}>{item.title}</div>
            <ul style={{ listStyle:"none", padding:0, margin:0, display:"flex", flexDirection:"column", gap:10 }}>
              {item.points.map((pt, i) => (
                <li key={i} style={{ display:"flex", gap:10, fontFamily:"'DM Sans', sans-serif", fontSize:13, color:T.muted, lineHeight:1.5 }}>
                  <span style={{ color: item.color, marginTop:2, flexShrink:0 }}>▸</span>{pt}
                </li>
              ))}
            </ul>
          </GlassCard>
        ))}
      </div>
    </Section>
  );
};

// ─── SKILLS SECTION ───────────────────────────────────────────────────────────
const SkillsSection = ({ skills }) => {
  const groups = skills.reduce((acc, s) => {
    const cat = s.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <Section id="skills">
      <SectionHeading label="Skills" title="Tools & Technologies." sub="A snapshot of what I work with daily." />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:20 }}>
        {Object.entries(groups).map(([cat, items], gi) => (
          <GlassCard key={cat} style={{ padding:28 }}>
            <div style={{
              fontFamily:"'Space Grotesk', sans-serif", fontSize:13, fontWeight:600,
              color:T.accent, textTransform:"uppercase", letterSpacing:"0.15em", marginBottom:24,
            }}>
              {cat}
            </div>
            {items.map((sk, i) => (
              <SkillBar key={sk.id || i} name={sk.name} level={sk.level} delay={i * 80 + gi * 40} />
            ))}
          </GlassCard>
        ))}
      </div>

      {/* Tech badge cloud */}
      {skills.length === 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginTop:32 }}>
          {["Cisco","Linux","Python","Zabbix","Wireshark","Docker","React","Node.js","MySQL","Git"].map(t => (
            <span key={t} style={{
              fontFamily:"'DM Sans', sans-serif", fontSize:13, fontWeight:500,
              padding:"8px 16px", borderRadius:999,
              background:T.blueDim, border:`1px solid rgba(59,130,246,0.2)`,
              color:T.blue,
            }}>{t}</span>
          ))}
        </div>
      )}
    </Section>
  );
};

// ─── EXPERIENCE SECTION ───────────────────────────────────────────────────────
const ExperienceSection = ({ experiences }) => {
  const [active, setActive] = useState(0);

  // Group multiple roles under the same company into one tab
  const raw = experiences.length ? experiences : [
    { id:1, company:"Example Corp", role:"NOC Engineer", start_date:"2022-01", end_date:null, is_current:1, points:[] },
  ];
  const companies = sortExperienceGrouped(raw).map(([company, roles]) => ({ company, roles }));
  const current = companies[active] || companies[0];

  return (
    <Section id="experience">
      <SectionHeading label="Experience" title="Where I've worked." />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:40 }} className="aurora-exp-grid">
        {/* Left — unique company list */}
        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
          {companies.map((c, i) => (
            <button key={c.company} onClick={() => setActive(i)}
              style={{
                fontFamily:"'DM Sans', sans-serif", fontWeight: active === i ? 600 : 400,
                fontSize:14, padding:"14px 20px", borderRadius:10,
                textAlign:"left", border:"none", cursor:"pointer",
                background: active === i ? T.accentDim : "transparent",
                borderLeft: active === i ? `2px solid ${T.accent}` : "2px solid transparent",
                color: active === i ? T.accent : T.muted,
                transition:"all 0.25s ease",
              }}
            >
              {c.company}
            </button>
          ))}
        </div>

        {/* Right — all roles at this company */}
        <GlassCard key={active} style={{ padding:36 }} hover={false}>
          <div style={{ animation:"aurora-in 0.4s cubic-bezier(0.16,1,0.3,1)" }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:20, paddingBottom:16,
              borderBottom:`1px solid ${T.border}` }}>
              <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:15, fontWeight:700, color:T.text }}>
                {current.company}
              </span>
              {(() => {
                const starts = current.roles.map(r => r.start_date).filter(Boolean);
                const hasCurrent = current.roles.some(r => r.is_current);
                const ends = current.roles.filter(r => !r.is_current).map(r => r.end_date).filter(Boolean);
                const earliestStart = starts.length ? starts.reduce((a, b) => a < b ? a : b) : null;
                const latestEnd = ends.length ? ends.reduce((a, b) => a > b ? a : b) : null;
                const d = calcDuration(earliestStart, latestEnd, hasCurrent);
                return d ? (
                  <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:10, color:T.accent,
                    background:"rgba(255,255,255,0.05)", borderRadius:6, padding:"2px 8px" }}>
                    {d} total
                  </span>
                ) : null;
              })()}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
              {current.roles.map((exp, i) => (
                <div key={exp.id || i}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
                    <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:11, color:T.accent,
                      fontWeight:600, textTransform:"uppercase", letterSpacing:"0.15em" }}>
                      {fmtDate(exp.start_date)} — {exp.is_current || !exp.end_date ? "Present" : fmtDate(exp.end_date)}
                    </span>
                    {calcDuration(exp.start_date, exp.end_date, exp.is_current) && (
                      <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:10, color:T.muted,
                        background:"rgba(255,255,255,0.05)", borderRadius:4, padding:"1px 6px" }}>
                        {calcDuration(exp.start_date, exp.end_date, exp.is_current)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontFamily:"'Space Grotesk', sans-serif",
                    fontSize:17, fontWeight:700, color:T.text, marginBottom: exp.employment_type ? 4 : 10 }}>
                    {exp.role}
                  </div>
                  {exp.employment_type && (
                    <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:11, fontWeight:600,
                      textTransform:"uppercase", letterSpacing:"0.12em", color:T.muted, marginBottom:10 }}>
                      {exp.employment_type}
                    </div>
                  )}
                  {(() => { const pts = Array.isArray(exp.points) ? exp.points : (typeof exp.points === "string" ? (() => { try { return JSON.parse(exp.points || "[]"); } catch { return []; } })() : []); return pts.length > 0 && (
                    <ul style={{ listStyle:"none", padding:0, margin:0, display:"flex", flexDirection:"column", gap:6 }}>
                      {pts.map((pt, j) => (
                        <li key={j} style={{ display:"flex", gap:10, alignItems:"flex-start",
                          fontFamily:"'DM Sans', sans-serif", fontSize:14, lineHeight:1.7, color:T.muted }}>
                          <span style={{ color:T.accent, marginTop:4, flexShrink:0 }}>▸</span>
                          {pt}
                        </li>
                      ))}
                    </ul>
                  ); })()}
                  <ExperienceMediaCards media={exp.media} cardStyle="dark" />
                  {current.roles.length > 1 && i < current.roles.length - 1 && (
                    <div style={{ height:1, background:T.border, marginTop:20 }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </GlassCard>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .aurora-exp-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
};

// ─── EDUCATION SECTION ────────────────────────────────────────────────────────
const EducationSection = ({ education }) => {
  const items = education.length ? education : [
    { id:1, institution:"Universitas Contoh", degree:"Bachelor of Information Technology", field:"Computer Science", start_year:"2019", end_year:"2023" },
  ];

  return (
    <Section id="education">
      <SectionHeading label="Education" title="Academic journey." />
      <div style={{ position:"relative" }}>
        {/* Timeline line */}
        <div style={{
          position:"absolute", left:20, top:0, bottom:0,
          width:1, background:`linear-gradient(to bottom, ${T.accent}, transparent)`,
        }} />

        <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
          {items.map((edu, i) => (
            <div key={edu.id || i} style={{ display:"flex", gap:40, paddingLeft:60, position:"relative" }}>
              {/* Dot */}
              <div style={{
                position:"absolute", left:12, top:24,
                width:17, height:17, borderRadius:"50%",
                background:`radial-gradient(circle, ${T.accent} 40%, ${T.accentDim} 100%)`,
                border:`3px solid ${T.bg}`,
                boxShadow:`0 0 10px ${T.accentDim}`,
              }} />

              <GlassCard style={{ padding:"24px 28px", flex:1 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8 }}>
                  <div>
                    <h3 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:17, fontWeight:700, color:T.text, marginBottom:4 }}>
                      {edu.degree}
                    </h3>
                    <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:T.muted }}>
                      {edu.institution} {edu.field ? `· ${edu.field}` : ""}
                    </div>
                  </div>
                  <span style={{
                    fontFamily:"'DM Sans', sans-serif", fontSize:12, fontWeight:600,
                    padding:"4px 12px", borderRadius:999,
                    background:T.accentDim, color:T.accent,
                    whiteSpace:"nowrap",
                  }}>
                    {edu.start_year} — {edu.end_year || "Present"}
                  </span>
                </div>
              </GlassCard>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ─── BLOG SECTION ─────────────────────────────────────────────────────────────
const BlogSection = ({ posts }) => {
  const displayed = posts.slice(0, 3);

  return (
    <Section id="blog">
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:48, flexWrap:"wrap", gap:16 }}>
        <SectionHeading label="Blog" title="Latest writing." sub="Thoughts and technical notes." />
        <Link to="/blog" style={{
          fontFamily:"'DM Sans', sans-serif", fontSize:13, fontWeight:600,
          color:T.accent, textDecoration:"none",
          display:"flex", alignItems:"center", gap:6,
        }}>
          All posts →
        </Link>
      </div>

      {displayed.length === 0 ? (
        <GlassCard style={{ padding:48, textAlign:"center" }} hover={false}>
          <p style={{ fontFamily:"'DM Sans', sans-serif", color:T.dim, fontSize:14 }}>No posts yet.</p>
        </GlassCard>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:20 }}>
          {displayed.map((post) => {
            const cover = resolveImg(post.cover_img);
            return (
              <Link key={post.id} to={`/blog/${post.slug}`} style={{ textDecoration:"none" }}>
                <GlassCard style={{ overflow:"hidden", height:"100%" }}>
                  {cover && (
                    <div style={{ height:180, overflow:"hidden" }}>
                      <img src={cover} alt={post.title} loading="lazy"
                        style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform 0.5s ease" }}
                        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                        onMouseLeave={e => e.currentTarget.style.transform = "none"}
                      />
                    </div>
                  )}
                  <div style={{ padding:"20px 24px" }}>
                    <h3 style={{
                      fontFamily:"'Space Grotesk', sans-serif",
                      fontSize:16, fontWeight:600, color:T.text, lineHeight:1.5,
                      marginBottom:8,
                    }}>
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p style={{
                        fontFamily:"'DM Sans', sans-serif",
                        fontSize:13, color:T.muted, lineHeight:1.7,
                        display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden",
                        marginBottom:16,
                      }}>
                        {post.excerpt}
                      </p>
                    )}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:11, color:T.dim }}>
                        {fmtDate(post.published_at)}
                      </span>
                      <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:T.accent, fontWeight:600 }}>
                        Read →
                      </span>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            );
          })}
        </div>
      )}
    </Section>
  );
};

// ─── PORTFOLIO SECTION ───────────────────────────────────────────────────────
const PortfolioSection = ({ projects }) => {
  const [showAll, setShowAll] = useState(false);
  if (!projects.length) return null;
  const visible = showAll ? projects : projects.slice(0, 6);
  return (
    <Section id="portfolio">
      <SectionHeading label="Portfolio" title="Things I've Built." />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:20 }}>
        {visible.map((proj, i) => {
          const img = proj.img ? (proj.img.startsWith("http") ? proj.img : `${BASE}${proj.img}`) : null;
          return (
            <a
              key={proj.id ?? i}
              href={proj.link || undefined}
              target={proj.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              style={{ textDecoration:"none", display:"flex", flexDirection:"column", cursor: proj.link ? "pointer" : "default" }}
            >
              <GlassCard hover={!!proj.link} style={{ padding:0, overflow:"hidden", display:"flex", flexDirection:"column", height:"100%" }}>
                {img ? (
                  <div style={{ height:160, overflow:"hidden", background:"rgba(255,255,255,0.04)" }}>
                    <img src={img} alt={proj.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  </div>
                ) : (
                  <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center", background:`linear-gradient(135deg, ${T.accent}15, transparent)` }}>
                    <svg width="40" height="40" fill="none" stroke={T.accent} strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div style={{ padding:"16px 20px 20px", flex:1, display:"flex", flexDirection:"column" }}>
                  <p style={{ color:T.text, fontWeight:700, fontSize:14, lineHeight:1.4, marginBottom:4 }}>{proj.name}</p>
                  {proj.category && (
                    <span style={{
                      marginTop:"auto", alignSelf:"flex-start",
                      fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase",
                      padding:"3px 10px", borderRadius:20,
                      background:`${T.accent}18`, color:T.accent, border:`1px solid ${T.accent}30`,
                    }}>
                      {proj.category}
                    </span>
                  )}
                </div>
              </GlassCard>
            </a>
          );
        })}
      </div>
      {projects.length > 6 && (
        <div style={{ display:"flex", justifyContent:"center", marginTop:32 }}>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              padding:"10px 28px", borderRadius:999,
              border:`1px solid ${T.accent}40`, color:T.accent,
              background:"transparent", fontSize:13, fontWeight:600,
              cursor:"pointer", transition:"all 0.2s",
            }}
          >
            {showAll ? "Show Less" : `Show All (${projects.length})`}
          </button>
        </div>
      )}
    </Section>
  );
};

// ─── CERTIFICATE SECTION ─────────────────────────────────────────────────────
const CertificateSection = ({ certificates }) => {
  const [showAll, setShowAll] = useState(false);
  if (!certificates.length) return null;
  const visible = showAll ? certificates : certificates.slice(0, 6);
  return (
    <Section id="certificates">
      <SectionHeading label="Certificates" title="Certifications & Credentials." />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:20 }}>
        {visible.map((cert, i) => {
          const img = cert.img ? (cert.img.startsWith("http") ? cert.img : `${BASE}${cert.img}`) : null;
          return (
            <a
              key={cert.id ?? i}
              href={cert.link || undefined}
              target={cert.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              style={{ textDecoration:"none", display:"flex", flexDirection:"column", cursor: cert.link ? "pointer" : "default" }}
            >
              <GlassCard hover={!!cert.link} style={{ padding:0, overflow:"hidden", display:"flex", flexDirection:"column", height:"100%" }}>
                {img ? (
                  <div style={{ height:140, overflow:"hidden", background:"rgba(255,255,255,0.04)" }}>
                    <img src={img} alt={cert.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  </div>
                ) : (
                  <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", background:`linear-gradient(135deg, ${T.accent}15, transparent)` }}>
                    <svg width="40" height="40" fill="none" stroke={T.accent} strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                )}
                <div style={{ padding:"16px 20px 20px", flex:1, display:"flex", flexDirection:"column" }}>
                  <p style={{ color:T.text, fontWeight:700, fontSize:14, lineHeight:1.4, marginBottom:4 }}>{cert.name}</p>
                  {cert.organizer && <p style={{ color:T.muted, fontSize:12, marginBottom:8 }}>{cert.organizer}</p>}
                  {cert.category && (
                    <span style={{
                      marginTop:"auto", alignSelf:"flex-start",
                      fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase",
                      padding:"3px 10px", borderRadius:20,
                      background:`${T.accent}18`, color:T.accent, border:`1px solid ${T.accent}30`,
                    }}>
                      {cert.category}
                    </span>
                  )}
                </div>
              </GlassCard>
            </a>
          );
        })}
      </div>
      {certificates.length > 6 && (
        <div style={{ display:"flex", justifyContent:"center", marginTop:32 }}>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              padding:"10px 28px", borderRadius:999,
              border:`1px solid ${T.accent}40`, color:T.accent,
              background:"transparent", fontSize:13, fontWeight:600,
              cursor:"pointer",
            }}
          >
            {showAll ? "Show Less" : `Show All (${certificates.length})`}
          </button>
        </div>
      )}
    </Section>
  );
};

// ─── CONTACT SECTION ──────────────────────────────────────────────────────────
const ContactSection = ({ profile }) => (
  <Section id="contact">
    <SectionHeading label="Contact" title="Let's work together." sub="Want to collaborate or just say hi? Reach out directly." />
    <div style={{ display:"flex", flexWrap:"wrap", gap:16, justifyContent:"center" }}>
      <a
        href={`https://wa.me/628997779254?text=${encodeURIComponent("Halo Affan, saya melihat portfolio kamu dan ingin terhubung.")}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration:"none" }}
      >
        <GlassCard style={{ padding:"18px 32px", display:"inline-flex", alignItems:"center", gap:12 }}>
          <svg width={20} height={20} fill="currentColor" style={{ color:"#22c55e" }} viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.523 5.854L.057 23.882a.5.5 0 00.611.641l6.193-1.624A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.693-.502-5.241-1.381l-.374-.217-3.876 1.017 1.035-3.78-.237-.389A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
          <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:15, fontWeight:700, color:T.text }}>WhatsApp</span>
        </GlassCard>
      </a>
      <a
        href={`mailto:affanhaidar232@gmail.com?subject=${encodeURIComponent("Hi Affan — from your portfolio")}&body=${encodeURIComponent("Halo Affan,\n\nSaya melihat portfolio kamu dan ingin terhubung.\n\n")}`}
        style={{ textDecoration:"none" }}
      >
        <GlassCard style={{ padding:"18px 32px", display:"inline-flex", alignItems:"center", gap:12 }}>
          <svg width={20} height={20} fill="none" stroke="currentColor" strokeWidth="2" style={{ color:T.accent }} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
          <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:15, fontWeight:700, color:T.text }}>Email</span>
        </GlassCard>
      </a>
    </div>
    <div style={{ marginTop:64, paddingTop:32, borderTop:`1px solid ${T.border}`, textAlign:"center", fontFamily:"'DM Sans', sans-serif", fontSize:13, color:T.dim }}>
      Built with ♥ by {profile?.full_name || "Affan Haidar"} · {new Date().getFullYear()}
    </div>
  </Section>
);

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const AuroraTheme = () => {
  const [profile,      setProfile]      = useState(null);
  const [skills,       setSkills]       = useState([]);
  const [education,    setEducation]    = useState([]);
  const [experiences,  setExperiences]  = useState([]);
  const [posts,        setPosts]        = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    injectFonts();
    injectStyles();
    trackVisit("/").catch(() => {});

    Promise.all([
      getProfile().catch(() => null),
      getSkills().catch(() => []),
      getEducation().catch(() => []),
      getExperience().catch(() => []),
      getPublishedPosts().catch(() => []),
      fetchCertificates().catch(() => []),
      fetchPortofolios().catch(() => []),
    ]).then(([p, sk, ed, ex, po, certs, projs]) => {
      setProfile(p ? (p.profile ?? p) : null);
      setSkills(Array.isArray(sk) ? sk : []);
      setEducation(Array.isArray(ed) ? ed : []);
      setExperiences(Array.isArray(ex) ? ex : []);
      setPosts(Array.isArray(po) ? po : []);
      setCertificates(Array.isArray(certs) ? certs : []);
      setProjects(Array.isArray(projs) ? projs : []);
    });
  }, []);

  // Scroll spy
  useEffect(() => {
    const sections = NAV_ITEMS.map(n => n.toLowerCase());
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { threshold: 0.3 }
    );
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{
      fontFamily:"'DM Sans', sans-serif",
      background:T.bg,
      color:T.text,
      minHeight:"100vh",
      position:"relative",
    }}>
      <AmbientBlobs />
      <Navbar activeSection={activeSection} />

      <main style={{ position:"relative", zIndex:1 }}>
        <HeroSection    profile={profile} />
        <AboutSection   profile={profile} />
        <WhatIDoSection profile={profile} />
        <SkillsSection  skills={skills} />
        <ExperienceSection experiences={experiences} />
        <EducationSection   education={education} />
        <PortfolioSection   projects={projects} />
        <CertificateSection certificates={certificates} />
        <BlogSection    posts={posts} />
        <ContactSection profile={profile} />
      </main>
    </div>
  );
};

export default AuroraTheme;
